import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BrainCircuit, ArrowRight, Sparkles, CheckCircle2, Loader2, Home, Clock, Target, Layers } from "lucide-react";

type Question = {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  options: {
    label: string;
    description: string;
    scores: { standard: number; deep: number; moveOut: number };
  }[];
};

const QUESTIONS: Question[] = [
  {
    id: "intent",
    title: "What is your primary goal?",
    subtitle: "Help us understand what you want to achieve.",
    icon: Target,
    options: [
      { label: "Routine Upkeep", description: "Maintain a consistently clean home", scores: { standard: 3, deep: 0, moveOut: 0 } },
      { label: "Deep Restore", description: "Tackle built-up grime and neglected areas", scores: { standard: 0, deep: 3, moveOut: 0 } },
      { label: "Moving In / Out", description: "Prepare an empty home for transition", scores: { standard: 0, deep: 0, moveOut: 4 } },
    ],
  },
  {
    id: "condition",
    title: "How would you describe the buildup?",
    subtitle: "Be honest—we've seen it all and judge nothing.",
    icon: Layers,
    options: [
      { label: "Light / Dusty", description: "Needs a good wipe down and vacuum", scores: { standard: 2, deep: 0, moveOut: 0 } },
      { label: "Moderate", description: "Some soap scum, grease, and pet hair", scores: { standard: 0, deep: 2, moveOut: 0 } },
      { label: "Heavy / Grime", description: "Significant buildup requiring scrubbing", scores: { standard: 0, deep: 3, moveOut: 0 } },
      { label: "Empty Home", description: "No furniture, just needs a full reset", scores: { standard: 0, deep: 0, moveOut: 2 } },
    ],
  },
  {
    id: "time",
    title: "When was the last professional deep clean?",
    subtitle: "This helps us estimate the baseline effort required.",
    icon: Clock,
    options: [
      { label: "Within 30 days", description: "Recently detailed", scores: { standard: 2, deep: 0, moveOut: 0 } },
      { label: "1-3 months ago", description: "Starting to accumulate buildup", scores: { standard: 1, deep: 1, moveOut: 0 } },
      { label: "3+ months ago", description: "Overdue for a detailed scrub", scores: { standard: 0, deep: 2, moveOut: 0 } },
      { label: "Never / Unsure", description: "Needs a complete baseline reset", scores: { standard: 0, deep: 3, moveOut: 0 } },
    ],
  },
  {
    id: "scope",
    title: "What is the scope of the cleaning?",
    subtitle: "Select the areas you want us to focus on.",
    icon: Home,
    options: [
      { label: "High-Traffic Areas", description: "Kitchen, living room, main bathrooms", scores: { standard: 2, deep: 0, moveOut: 0 } },
      { label: "Full Home", description: "Every room, top to bottom", scores: { standard: 1, deep: 2, moveOut: 0 } },
      { label: "Empty Reset", description: "Inside cabinets, appliances, all surfaces", scores: { standard: 0, deep: 0, moveOut: 3 } },
    ],
  },
];

const PROCESSING_STEPS = [
  "Analyzing home condition...",
  "Evaluating buildup levels...",
  "Matching with service scopes...",
  "Calculating confidence score...",
  "Generating final recommendation..."
];

export function ServiceRecommendationEngine() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({ standard: 0, deep: 0, moveOut: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingTextIndex, setProcessingTextIndex] = useState(0);
  const [result, setResult] = useState<{ package: string; confidence: number; reason: string } | null>(null);

  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [intervalId]);

  useEffect(() => {
    const handleOpenEngine = () => setIsOpen(true);
    window.addEventListener('open-recommendation-engine', handleOpenEngine);
    return () => window.removeEventListener('open-recommendation-engine', handleOpenEngine);
  }, []);

  const handleOptionSelect = (optionScores: { standard: number; deep: number; moveOut: number }) => {
    const newScores = {
      standard: scores.standard + optionScores.standard,
      deep: scores.deep + optionScores.deep,
      moveOut: scores.moveOut + optionScores.moveOut,
    };
    setScores(newScores);

    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      startProcessing(newScores);
    }
  };

  const startProcessing = (finalScores: { standard: number; deep: number; moveOut: number }) => {
    setIsProcessing(true);
    setStep(QUESTIONS.length);

    let currentTextIndex = 0;
    const id = setInterval(() => {
      currentTextIndex++;
      if (currentTextIndex < PROCESSING_STEPS.length) {
        setProcessingTextIndex(currentTextIndex);
      } else {
        clearInterval(id);
        generateResult(finalScores);
      }
    }, 600);
    setIntervalId(id);
  };

  const generateResult = (finalScores: { standard: number; deep: number; moveOut: number }) => {
    let recommended = "Standard Clean";
    let reason = "This recommendation is based on your goal of routine upkeep, lighter buildup levels, and regular cleaning schedule.";
    
    if (finalScores.moveOut > finalScores.standard && finalScores.moveOut > finalScores.deep) {
      recommended = "Move In/Out Clean";
      reason = "This recommendation is based on your transition needs, an empty home environment, and the requirement for a full reset including inside cabinets and appliances.";
    } else if (finalScores.deep > finalScores.standard) {
      recommended = "Deep Clean";
      reason = "This recommendation is based on the time since your last professional clean, moderate-to-heavy buildup levels, and your goal to deeply restore the home.";
    }

    // Generate a realistic confidence score between 88 and 98
    const totalPoints = finalScores.standard + finalScores.deep + finalScores.moveOut;
    const maxPoints = Math.max(finalScores.standard, finalScores.deep, finalScores.moveOut);
    const calculatedConfidence = Math.min(98, Math.max(88, Math.round((maxPoints / totalPoints) * 100) + 15));

    setResult({
      package: recommended,
      confidence: calculatedConfidence,
      reason: reason,
    });
    setIsProcessing(false);
  };

  const reset = () => {
    setStep(0);
    setScores({ standard: 0, deep: 0, moveOut: 0 });
    setResult(null);
    setIsProcessing(false);
    setProcessingTextIndex(0);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTimeout(reset, 300); // Reset after close animation
    }
    setIsOpen(open);
  };

  return (
    <>
      {/* Container for the Recommendation Engine */}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
<div className="flex justify-center w-full mt-16 mb-12 relative z-20 px-4 md:px-0">
          <DialogTrigger asChild>
            <div 
              role="button"
              tabIndex={0}
              className="group cursor-pointer relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary to-[#0073a8] dark:from-primary dark:to-[#004766] text-primary-foreground shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 h-auto w-full max-w-5xl mx-auto flex flex-col md:flex-row items-stretch border-4 border-white/10 dark:border-white/5 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 whitespace-normal text-left"
            >
              {/* Decorative background effects */}
              <div className="absolute inset-0 w-full h-full bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_50%,transparent_75%)] bg-[length:250%_250%,100%_100%] animate-[shimmer_3s_linear_infinite] opacity-30"></div>
              <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-colors duration-500"></div>
              
              <div className="flex flex-col items-start justify-center p-6 md:p-10 lg:p-12 text-left z-10 flex-1 w-full min-w-0">
                <span className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white/20 text-white text-xs md:text-sm font-bold uppercase tracking-wider mb-4 border border-white/30 backdrop-blur-sm">
                  <BrainCircuit className="mr-2 h-4 w-4 shrink-0" /> <span className="whitespace-nowrap">AI Matchmaker</span>
                </span>
                <span className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight mb-3 text-white drop-shadow-md leading-tight break-words whitespace-normal w-full">
                  Need Help Choosing?
                </span>
                <span className="text-base md:text-lg lg:text-xl font-medium text-white/90 max-w-2xl leading-relaxed whitespace-normal break-words">
                  Take our Quick quiz to find the perfect cleaning package for your home and budget.
                </span>
              </div>

              <div className="p-6 md:p-10 lg:p-12 shrink-0 flex items-center justify-center z-10 bg-black/10 md:bg-black/5 border-t border-white/10 md:border-t-0 md:border-l w-full md:w-auto">
                <div className="inline-flex items-center justify-center bg-primary-foreground text-primary rounded-2xl px-6 py-4 md:px-10 md:py-6 font-bold text-lg md:text-xl shadow-lg group-hover:scale-105 group-hover:shadow-xl transition-all duration-300 whitespace-nowrap">
                  Find My Package
                  <ArrowRight className="ml-3 h-5 w-5 md:h-6 md:w-6 group-hover:translate-x-1.5 transition-transform shrink-0" />
                </div>
              </div>
            </div>
          </DialogTrigger>
        </div>

        <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden bg-background border-border/50 shadow-2xl rounded-3xl">
          <DialogTitle className="sr-only">Service Recommendation Engine</DialogTitle>
          
          {/* Progress Bar */}
          {!result && !isProcessing && (
            <div className="w-full bg-secondary h-1.5">
              <div 
                className="bg-primary h-full transition-all duration-500 ease-out"
                style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
              />
            </div>
          )}

          <div className="p-6 sm:p-10 min-h-[400px] flex flex-col relative">
            
            {/* QA State */}
            {!isProcessing && !result && step < QUESTIONS.length && (
              <div className="flex-1 flex flex-col animate-in fade-in duration-500">
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-primary/10 rounded-xl text-primary">
                      {(() => {
                        const Icon = QUESTIONS[step].icon;
                        return <Icon className="w-6 h-6" />;
                      })()}
                    </div>
                    <span className="text-sm font-bold tracking-wider uppercase text-primary/80">Step {step + 1} of {QUESTIONS.length}</span>
                  </div>
                  <h2 className="text-3xl font-extrabold text-foreground mb-2 leading-tight tracking-tight">
                    {QUESTIONS[step].title}
                  </h2>
                  <p className="text-muted-foreground text-lg">
                    {QUESTIONS[step].subtitle}
                  </p>
                </div>

                <div className="grid gap-3 mt-auto">
                  {QUESTIONS[step].options.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleOptionSelect(option.scores)}
                      className="group flex flex-col items-start p-5 rounded-2xl border-2 border-border/60 bg-card hover:border-primary hover:bg-primary/5 transition-all duration-200 text-left"
                    >
                      <span className="font-bold text-lg text-foreground group-hover:text-primary transition-colors">
                        {option.label}
                      </span>
                      <span className="text-sm text-muted-foreground mt-1">
                        {option.description}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Processing State */}
            {isProcessing && (
              <div className="flex-1 flex flex-col items-center justify-center text-center animate-in fade-in duration-500 py-12">
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
                  <div className="bg-card border border-border p-5 rounded-full relative z-10 shadow-lg">
                    <BrainCircuit className="w-12 h-12 text-primary animate-pulse" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-3">AI Analysis in Progress</h3>
                <div className="h-6 overflow-hidden relative w-full max-w-[250px]">
                  {PROCESSING_STEPS.map((text, idx) => (
                    <div 
                      key={idx}
                      className={`absolute inset-0 transition-all duration-500 flex items-center justify-center text-muted-foreground font-medium
                        ${idx === processingTextIndex ? 'opacity-100 translate-y-0' : 
                          idx < processingTextIndex ? 'opacity-0 -translate-y-full' : 'opacity-0 translate-y-full'}`}
                    >
                      {text}
                    </div>
                  ))}
                </div>
                <Progress value={(processingTextIndex / (PROCESSING_STEPS.length - 1)) * 100} className="h-1.5 w-48 mt-8" />
              </div>
            )}

            {/* Result State */}
            {result && (
              <div className="flex-1 flex flex-col animate-in slide-in-from-bottom-4 fade-in duration-500">
                <div className="flex items-center justify-between mb-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-bold border border-emerald-500/20">
                    <Sparkles className="w-4 h-4" />
                    Analysis Complete
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-primary tracking-tighter">{result.confidence}%</div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Match Score</div>
                  </div>
                </div>

                <div className="bg-secondary/50 rounded-3xl p-8 border border-border mb-8">
                  <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-2">Recommended Package</p>
                  <h2 className="text-4xl font-black text-foreground mb-4 tracking-tight">
                    {result.package}
                  </h2>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed text-lg">
                      {result.reason}
                    </p>
                  </div>
                </div>

                <div className="mt-auto pt-4">
                  <Button 
                    className="w-full h-16 text-lg font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                    onClick={() => {
                      setIsOpen(false);
                      const serviceMap: Record<string, "standard" | "deep" | "move"> = {
                        "Standard Clean": "standard",
                        "Deep Clean": "deep",
                        "Move In/Out Clean": "move"
                      };
                      const recommendedService = serviceMap[result.package] || "standard";
                      
                      // Update calculator state via localStorage and event
                      try {
                        const raw = localStorage.getItem("uplevel_calc_state");
                        const current = raw ? JSON.parse(raw) : {};
                        const next = { ...current, serviceType: recommendedService };
                        localStorage.setItem("uplevel_calc_state", JSON.stringify(next));
                        window.dispatchEvent(new CustomEvent("uplevel_calc_sync", { detail: next }));
                      } catch (e) {}

                      window.location.href = "/book?view=custom-plan";
                    }}
                  >
                    Continue to Booking
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full mt-2 text-muted-foreground hover:text-foreground transition-all duration-300 hover:bg-secondary/50 hover:scale-[1.02] animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300"
                    onClick={reset}
                  >
                    Retake Quiz
                  </Button>
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    Takes less than 60 seconds. No credit card required to hold date.
                  </p>
                </div>
              </div>
            )}

          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
